import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import Close from "../../../../../icons/close";
import Save from "../../../../../icons/save";
import Trash from "../../../../../icons/trash";
import Papa from "papaparse";
import Swal from "sweetalert2";

interface Props {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Farmacia {
  farmacia_id: number;
  nombre: string;
}

interface Product {
  producto_id: number;
  nombre: string;
  cantidad: number;
  articulo: string;
  unidad: string;
}

/** Quitar BOM, espacios, mayúsculas/acentos para comparar cabeceras (Excel, export CSV) */
const normalizeHeaderKey = (k: string) =>
  k
    .replace(/^\uFEFF/, "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/** Resuelve Articulo / ﻿Articulo (BOM) / artículo / "Articulo " y cantidad */
function getArticuloCantidadFromRow(row: Record<string, unknown>): {
  articulo: string;
  cantidad: number;
} | null {
  let articuloVal: string | undefined;
  let cantVal: string | number | undefined;

  for (const [key, raw] of Object.entries(row)) {
    if (key == null || raw === undefined || raw === null) {
      continue;
    }
    const h = normalizeHeaderKey(String(key));
    if (!h) {
      continue;
    }
    if (h === "articulo" || h.startsWith("articul") || h === "sku" || h === "codigo") {
      articuloVal = sanitizeCsvCell(raw);
    } else if (
      h === "cantidad" ||
      h.startsWith("cantid") ||
      h === "qty" ||
      h === "quantity"
    ) {
      cantVal = typeof raw === "number" ? raw : String(raw);
    }
  }

  if (!articuloVal) {
    return null;
  }
  const n = Number(
    String(cantVal ?? "")
      .replace(",", ".")
      .replace(/\s/g, "")
  );
  if (Number.isNaN(n) || n === 0) {
    return null;
  }
  return { articulo: articuloVal, cantidad: n };
}

const articuloKeyMatch = (a: string, b: string) =>
  String(a)
    .trim()
    .localeCompare(String(b).trim(), undefined, { sensitivity: "base" }) === 0;

const sanitizeCsvCell = (v: unknown) =>
  String(v ?? "")
    .replace(/\r/g, "")
    .replace(/\u00a0/g, " ")
    .trim();

/** Empareja el valor de la columna Articulo con artículo, nombre o ID de producto */
function findProductoEnList(list: Product[], csvValue: string): Product | undefined {
  const s = sanitizeCsvCell(csvValue);
  if (!s) {
    return undefined;
  }
  const byArt = list.find(
    (p) => p.articulo != null && articuloKeyMatch(String(p.articulo), s)
  );
  if (byArt) {
    return byArt;
  }
  const byNom = list.find(
    (p) => p.nombre && articuloKeyMatch(p.nombre, s)
  );
  if (byNom) {
    return byNom;
  }
  const asNum = Number(s);
  if (!Number.isNaN(asNum) && s !== "") {
    return list.find((p) => p.producto_id === asNum);
  }
  return undefined;
}

const MultiFarmaciaInventory = ({ isVisible, setIsVisible }: Props) => {
  const [farmacias, setFarmacias] = useState<Farmacia[]>([]);
  const [selectedFarmaciaIds, setSelectedFarmaciaIds] = useState<Set<number>>(
    new Set()
  );
  const [productList, setProductList] = useState<Product[]>([]);
  const [tempProducto, setTempProducto] = useState<Partial<Product>>({});
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

  const getFarmacias = useCallback(async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/farmacias/allFarmacias`,
      {
        headers: {
          "Content-Type": "application/json",
          "auth-token": `${sessionStorage.getItem("token")}`,
        },
      }
    );
    setFarmacias(response.data);
  }, []);

  const getProductos = useCallback(async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/productos/active`,
      {
        headers: { "auth-token": sessionStorage.getItem("token") },
      }
    );
    setProductList(response.data);
  }, []);

  useEffect(() => {
    if (isVisible) {
      getFarmacias();
      getProductos();
    }
  }, [isVisible, getFarmacias, getProductos]);

  const toggleFarmacia = (id: number) => {
    setSelectedFarmaciaIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllFarmacias = () => {
    setSelectedFarmaciaIds(
      new Set(farmacias.map((f) => f.farmacia_id))
    );
  };

  const clearFarmacias = () => setSelectedFarmaciaIds(new Set());

  const closeDrawer = () => {
    setSelectedProducts([]);
    setSelectedFarmaciaIds(new Set());
    setTempProducto({});
    setIsVisible(false);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setTempProducto((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductoChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const selectedNombre = event.target.value;
    const foundProduct = productList.find(
      (producto) => producto.nombre === selectedNombre
    );
    if (!foundProduct) {
      return;
    }
    setTempProducto({
      ...tempProducto,
      nombre: foundProduct.nombre,
      producto_id: foundProduct.producto_id,
    });
  };

  const newProduct = () => {
    if (!tempProducto.producto_id) {
      return;
    }
    const selectedProduct = productList.find(
      (p) => p.producto_id === tempProducto.producto_id
    );
    if (!selectedProduct) {
      return;
    }
    const cant = Number(tempProducto.cantidad);
    if (Number.isNaN(cant) || cant === 0) {
      return;
    }
    setSelectedProducts((prev) => {
      const i = prev.findIndex((p) => p.producto_id === tempProducto.producto_id);
      if (i >= 0) {
        const nextQ = Number(prev[i].cantidad) + cant;
        if (nextQ <= 0) {
          return prev.filter((p) => p.producto_id !== tempProducto.producto_id);
        }
        const next = [...prev];
        next[i] = { ...next[i], cantidad: nextQ };
        return next;
      }
      return [
        ...prev,
        {
          producto_id: selectedProduct.producto_id,
          nombre: selectedProduct.nombre,
          cantidad: cant,
          articulo: selectedProduct.articulo,
          unidad: selectedProduct.unidad,
        },
      ];
    });
  };

  const openFileSelector = () => {
    const fileInput = document.getElementById(
      "multiFarmInvCsv"
    ) as HTMLInputElement | null;
    fileInput?.click();
  };

  const updateSelectedProductsFromCSV = (rows: Record<string, unknown>[]) => {
    if (!productList.length) {
      void Swal.fire({
        icon: "warning",
        title: "Cargue la lista de productos",
        text: "Espere un momento e intente de nuevo a subir el archivo.",
        confirmButtonText: "Aceptar",
      });
      return;
    }
    const productUpdates = rows
      .map((row) => getArticuloCantidadFromRow(row))
      .filter((u): u is { articulo: string; cantidad: number } => u !== null);

    if (productUpdates.length === 0) {
      void Swal.fire({
        icon: "info",
        title: "No se importaron filas",
        text: "Use encabezados Articulo y Cantidad, y valores numéricos en cantidad. Si exportó desde Excel, guarde como CSV o espere a que carguen los productos del sistema.",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    const notFound: string[] = [];
    setSelectedProducts((prev) => {
      const updated = [...prev];
      for (const update of productUpdates) {
        const full = findProductoEnList(productList, update.articulo);
        if (!full) {
          notFound.push(update.articulo);
          continue;
        }
        const productIndex = updated.findIndex(
          (p) => p.producto_id === full.producto_id
        );
        if (productIndex > -1) {
          updated[productIndex] = {
            ...updated[productIndex],
            cantidad: updated[productIndex].cantidad + update.cantidad,
          };
        } else {
          updated.push({ ...full, cantidad: update.cantidad });
        }
      }
      return updated;
    });
    if (notFound.length) {
      void Swal.fire({
        icon: "info",
        title: "Algunas filas no coinciden con productos activos",
        text: `Valores en CSV sin match (use código de artículo, nombre de producto o ID): ${notFound.join(
          ", "
        )}.`,
        confirmButtonText: "Aceptar",
      });
    }
  };

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        transformHeader: (header) => header.replace(/^\uFEFF/g, "").trim(),
        // Excel (regional) suele separar con ";"
        delimitersToGuess: [",", ";", "\t", "|"],
        complete: (results) => {
          const data = (results.data as Record<string, unknown>[]) || [];
          updateSelectedProductsFromCSV(data);
        },
        error: (err) => {
          void Swal.fire({
            icon: "error",
            title: "Error al leer el CSV",
            text: err.message || "No se pudo analizar el archivo",
          });
        },
      });
    }
    event.target.value = "";
  };

  const removeProductById = (id: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.producto_id !== id));
  };

  const handleSubmit = async () => {
    if (selectedFarmaciaIds.size === 0) {
      await Swal.fire({
        icon: "warning",
        title: "Seleccione al menos una farmacia",
        confirmButtonText: "Aceptar",
      });
      return;
    }
    if (selectedProducts.length === 0) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "No hay productos que agregar",
        confirmButtonText: "Aceptar",
      });
      return;
    }
    const f_armacias = Array.from(selectedFarmaciaIds);
    const f_productos = selectedProducts.map((p) => ({
      producto_id: p.producto_id,
      cantidad: Number(p.cantidad),
    }));
    try {
      Swal.fire({
        title: "Procesando...",
        text: "No cierre esta ventana.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/producto_farmacia/sync-catalogo`,
        { f_armacias, f_productos },
        {
          headers: {
            "Content-Type": "application/json",
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );
      Swal.close();
      await Swal.fire({
        icon: "success",
        title: "Catálogo sincronizado",
        text: `Aplicado a ${f_armacias.length} farmacia(s) con ${f_productos.length} producto(s).`,
        confirmButtonText: "Aceptar",
      });
      closeDrawer();
    } catch (err) {
      Swal.close();
      let message = "No se pudo sincronizar el catálogo";
      if (axios.isAxiosError(err) && err.response?.data) {
        const d = err.response.data as { message?: string };
        if (typeof d === "string") {
          message = d;
        } else if (d.message) {
          message = d.message;
        }
      }
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
        confirmButtonText: "Aceptar",
      });
    }
  };

  if (!isVisible) {
    return null;
  }

  const nSelected = selectedFarmaciaIds.size;

  return (
    <div className="bg-drawer">
      <div className="grow" />
      <div className="h-full bg-white p-2 w-screen md:w-1/3 px-6 max-h-screen overflow-y-auto">
        <div className="w-full sticky top-0 bg-white flex items-center my-4 z-10">
          <p className="text-xl">
            <strong>Modificar varias farmacias</strong>
          </p>
          <div className="grow" />
          <button
            type="button"
            className="p-1"
            onClick={handleSubmit}
            aria-label="Guardar"
          >
            <Save size={32} />
          </button>
          <button
            type="button"
            className="p-1 ml-1"
            onClick={closeDrawer}
            aria-label="Cerrar"
          >
            <Close />
          </button>
        </div>

        <div className="flex flex-col gap-4 w-full">
          <div>
            <p className="text-sm text-gray-600 mb-1">
              Seleccione las farmacias que recibirán el mismo ajuste de
              inventario
            </p>
            <details className="border border-gray-300 rounded-xl overflow-hidden">
              <summary className="px-4 py-3 cursor-pointer bg-[#F3F3F3] font-medium list-none [&::-webkit-details-marker]:hidden">
                Farmacias ({nSelected} seleccionada{nSelected === 1 ? "" : "s"}
                )
              </summary>
              <div className="flex justify-end gap-2 px-2 py-1 border-b border-gray-200">
                <button
                  type="button"
                  className="text-sm text-[#205f43] font-semibold"
                  onClick={selectAllFarmacias}
                >
                  Marcar todas
                </button>
                <button
                  type="button"
                  className="text-sm text-gray-500"
                  onClick={clearFarmacias}
                >
                  Desmarcar
                </button>
              </div>
              <div
                className="p-2 max-h-48 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {farmacias.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">
                    Cargando farmacias…
                  </p>
                ) : (
                  farmacias.map((f) => (
                    <label
                      key={f.farmacia_id}
                      className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-0 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFarmaciaIds.has(f.farmacia_id)}
                        onChange={() => toggleFarmacia(f.farmacia_id)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span className="text-sm flex-1">{f.nombre}</span>
                    </label>
                  ))
                )}
              </div>
            </details>
          </div>

          <div className="border-t border-gray-200 pt-2">
            <p className="font-medium mb-2">Productos</p>
            <div>
              <label htmlFor="multi-farm-producto">Producto</label>
              <input
                type="text"
                id="multi-farm-producto"
                name="producto"
                onChange={handleProductoChange}
                list="multi-farm-product-names"
                className="main-input w-full"
                autoComplete="off"
              />
              <datalist id="multi-farm-product-names">
                {productList.map((p) => (
                  <option key={p.producto_id} value={p.nombre} />
                ))}
              </datalist>
            </div>
            <div className="mt-2">
              <label htmlFor="multi-farm-cantidad">Cantidad</label>
              <input
                type="number"
                id="multi-farm-cantidad"
                name="cantidad"
                value={tempProducto.cantidad ?? ""}
                onChange={handleInputChange}
                className="main-input"
              />
            </div>
            <button
              type="button"
              className="cefa-button w-auto mt-2"
              onClick={newProduct}
            >
              Agregar
            </button>
          </div>

          <div
            className="border border-gray-300 rounded-lg shadow-md flex flex-col items-center cursor-pointer"
            onClick={openFileSelector}
            role="presentation"
          >
            <p className="p-4">Click para subir reporte (CSV: Articulo, Cantidad)</p>
            <input
              id="multiFarmInvCsv"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCSVUpload}
            />
          </div>

          <div
            className="overflow-auto bg-white pb-4"
            style={{ maxHeight: "min(50vh, 400px)" }}
          >
            <table className="w-full">
              <thead className="bg-gray-50 top-0 sticky">
                <tr className="table-head text-left">
                  <th>ID</th>
                  <th>Nombre</th>
                  <th className="text-center">Cantidad</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#FAFAFA]">
                {selectedProducts.length === 0 ? (
                  <tr className="hover:bg-slate-100">
                    <td className="text-center" colSpan={4}>
                      No hay productos seleccionados
                    </td>
                  </tr>
                ) : (
                  selectedProducts.map((p) => (
                    <tr
                      className="hover:bg-slate-100 table-body"
                      key={p.producto_id}
                    >
                      <td>{p.producto_id}</td>
                      <td>{p.nombre}</td>
                      <td className="text-center">{p.cantidad}</td>
                      <td>
                        <div className="m-auto w-fit">
                          <button
                            type="button"
                            onClick={() => removeProductById(p.producto_id)}
                          >
                            <Trash size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiFarmaciaInventory;
